/**
 * Shared inspector control kit (ADR-0010 S15c) — the Elementor-style layout
 * primitives used by BOTH the Style panel and the Properties panel so the two
 * read as one tidy, ordered inspector:
 *
 *  - `Section`   — a small bold sub-header that groups related rows.
 *  - `Field`     — one labelled row. `layout="row"` puts the control on the
 *                  right of the label (compact controls: select / segmented /
 *                  switch / swatch); `layout="stack"` puts a full-width control
 *                  under the label (text / expression / multi-field editors).
 *                  Each row draws a hairline separator, like Elementor.
 *  - `Segmented` — a button group (no popup) for small choice sets / icons.
 *  - `Swatch`    — a row of colour dots.
 *  - `Select`    — a CUSTOM dropdown (NOT a native `<select>` — the user's
 *                  standing rule) for longer / dynamic option lists.
 */
import { Check, ChevronDown, Link2, Unlink2 } from "lucide-react";
import { type ReactNode, useRef, useState } from "react";

import { type DimValue, DIM_SIDES, DIM_UNITS } from "@/lib/pageStyle";

import { FloatingPopover } from "./FloatingPopover";

export interface KitOption {
  value: string;
  label: string;
  /** Optional glyph shown instead of the label (segmented icon controls). */
  icon?: ReactNode;
  /** Optional swatch-preview class (colour pickers). */
  swatch?: string;
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
        {title}
      </p>
      {children}
    </div>
  );
}

export function Field({
  label,
  hint,
  layout = "row",
  children,
}: {
  label: string;
  hint?: string;
  layout?: "row" | "stack";
  children: ReactNode;
}) {
  if (layout === "stack") {
    return (
      <div className="flex flex-col gap-1.5 border-b border-border/50 py-2 last:border-0">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        {children}
        {hint && <p className="text-[11px] leading-snug text-muted-foreground/80">{hint}</p>}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/50 py-2 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

export function Segmented({
  value,
  options,
  defaultValue,
  onChange,
}: {
  value: string;
  options: KitOption[];
  /** When the chosen value equals this, `onChange` receives `null` (clear). */
  defaultValue?: string;
  onChange: (value: string | null) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-border">
      {options.map((o, i) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            title={o.label}
            aria-label={o.label}
            aria-pressed={active}
            onClick={() => onChange(o.value === defaultValue ? null : o.value)}
            className={`flex min-w-[26px] items-center justify-center px-1.5 py-1 text-[11px] font-medium leading-none ${
              i > 0 ? "border-l border-border" : ""
            } ${
              active
                ? "bg-accent text-accent-foreground"
                : "bg-background text-muted-foreground hover:text-foreground"
            }`}
          >
            {o.icon ?? o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Swatch({
  value,
  options,
  defaultValue,
  onChange,
}: {
  value: string;
  options: KitOption[];
  defaultValue?: string;
  onChange: (value: string | null) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            title={o.label}
            aria-label={o.label}
            aria-pressed={active}
            onClick={() => onChange(o.value === defaultValue ? null : o.value)}
            className={`h-5 w-5 rounded-full ${o.swatch ?? ""} ${
              active ? "ring-2 ring-accent ring-offset-1 ring-offset-surface" : "ring-1 ring-border"
            }`}
          />
        );
      })}
    </div>
  );
}

/** A custom dropdown — NEVER a native `<select>`. Options render in-DOM only
 *  while open (closed by an outside click), each as a `role="option"` button. */
export function Select({
  value,
  options,
  placeholder = "—",
  onChange,
}: {
  value: string;
  options: KitOption[];
  placeholder?: string;
  onChange: (value: string) => void;
  /** Kept for call-site compat; placement is now handled by `FloatingPopover`. */
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const current = options.find((o) => o.value === value);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex min-w-[120px] items-center justify-between gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-xs"
      >
        <span className={current ? "" : "text-muted-foreground"}>{current?.label ?? placeholder}</span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      <FloatingPopover anchorRef={btnRef} open={open} onClose={() => setOpen(false)}>
        <div
          role="listbox"
          className="portal-pop max-h-60 w-44 overflow-auto rounded-md border border-border bg-elevated p-1 shadow-xl"
        >
          {options.length === 0 && (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">No options</p>
          )}
          {options.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs ${
                  active ? "bg-accent/15 text-foreground" : "text-muted-foreground hover:bg-accent/10 hover:text-foreground"
                }`}
              >
                <span>{o.label}</span>
                {active && <Check className="h-3.5 w-3.5 text-accent" />}
              </button>
            );
          })}
        </div>
      </FloatingPopover>
    </div>
  );
}

const SIDE_LABELS: Record<string, string> = {
  top: "Top",
  right: "Right",
  bottom: "Bottom",
  left: "Left",
};

/** Elementor-style spacing control: four numeric inputs (Top/Right/Bottom/Left)
 *  + a LINK toggle to edit all sides at once + a unit selector. Emits a
 *  `DimValue` (or `undefined` when every side is cleared). */
export function DimensionField({
  value,
  onChange,
}: {
  value?: DimValue;
  onChange: (v?: DimValue) => void;
}) {
  const v = value ?? {};
  const unit = typeof v.unit === "string" && DIM_UNITS.includes(v.unit as (typeof DIM_UNITS)[number]) ? v.unit : DIM_UNITS[0];
  const defined = DIM_SIDES.map((s) => v[s]).filter((x): x is number => x != null);
  const [linked, setLinked] = useState(() => defined.length > 1 && defined.every((x) => x === defined[0]));

  const emit = (next: DimValue) => {
    const pruned: DimValue = {};
    let any = false;
    for (const s of DIM_SIDES) {
      if (next[s] != null && Number.isFinite(next[s])) {
        pruned[s] = next[s];
        any = true;
      }
    }
    if (!any) {
      onChange(undefined);
      return;
    }
    pruned.unit = unit;
    onChange(pruned);
  };

  const setSide = (side: (typeof DIM_SIDES)[number], raw: string) => {
    const num = raw === "" ? undefined : Number(raw);
    if (linked) {
      const next: DimValue = {};
      for (const s of DIM_SIDES) next[s] = num;
      emit(next);
    } else {
      emit({ ...v, [side]: num });
    }
  };

  const setUnit = (u: string) => {
    if (defined.length === 0) {
      onChange(undefined);
      return;
    }
    const next: DimValue = { unit: u };
    for (const s of DIM_SIDES) if (v[s] != null) next[s] = v[s];
    onChange(next);
  };

  const inputCls =
    "w-full min-w-0 rounded-md border border-border bg-background px-1 py-1.5 text-center text-xs";

  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-end">
        <Segmented value={unit} options={DIM_UNITS.map((u) => ({ value: u, label: u }))} onChange={(u) => u && setUnit(u)} />
      </div>
      <div className="flex items-stretch gap-1">
        {DIM_SIDES.map((s) => (
          <input
            key={s}
            type="number"
            aria-label={SIDE_LABELS[s]}
            value={v[s] ?? ""}
            onChange={(e) => setSide(s, e.target.value)}
            className={inputCls}
          />
        ))}
        <button
          type="button"
          aria-label="Link sides"
          aria-pressed={linked}
          title={linked ? "Sides linked — edit all at once" : "Edit each side separately"}
          onClick={() => setLinked((l) => !l)}
          className={`flex w-7 shrink-0 items-center justify-center rounded-md border ${
            linked ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground"
          }`}
        >
          {linked ? <Link2 className="h-3.5 w-3.5" /> : <Unlink2 className="h-3.5 w-3.5" />}
        </button>
      </div>
      <div className="flex gap-1">
        {DIM_SIDES.map((s) => (
          <span key={s} className="flex-1 text-center text-[10px] text-muted-foreground/70">
            {SIDE_LABELS[s]}
          </span>
        ))}
        <span className="w-7 shrink-0" />
      </div>
    </div>
  );
}
